import { useCallback, useEffect, useState } from 'react';
import { TopBar } from './app/TopBar';
import { PanelSection } from './ui/panels/PanelSection';
import { UnsupportedDialog } from './ui/dialogs/UnsupportedDialog';
import { Viewer } from './ui/canvas/Viewer';
import { HistogramView } from './ui/panels/HistogramView';
import { LightPanel } from './ui/panels/LightPanel';
import { WbPanel } from './ui/panels/WbPanel';
import { GeometryPanel } from './ui/panels/GeometryPanel';
import { ColorPanel } from './ui/panels/ColorPanel';
import { HslPanel } from './ui/panels/HslPanel';
import { CurvePanel } from './ui/panels/CurvePanel';
import { DetailPanel } from './ui/panels/DetailPanel';
import { MasksPanel } from './ui/panels/MasksPanel';
import { HealPanel } from './ui/panels/HealPanel';
import { SkinPanel } from './ui/panels/SkinPanel';
import { CleanWhitesPanel } from './ui/panels/CleanWhitesPanel';
import { BackgroundPanel } from './ui/panels/BackgroundPanel';
import { LutPanel } from './ui/panels/LutPanel';
import { FoodPanel } from './ui/panels/FoodPanel';
import { EffectsPanel } from './ui/panels/EffectsPanel';
import { UpscalePanel } from './ui/panels/UpscalePanel';
import { AiCompareDialog, AiPhotoButtons, AiSettingsDialog } from './ui/dialogs/AiDialogs';
import { useAiUi } from './features/ai/aiActions';
import { PlatingWorkspace } from './ui/plating/PlatingWorkspace';
import { SimplePanel } from './ui/simple/SimplePanel';
import { AboutDialog, CheatSheet } from './ui/dialogs/InfoDialogs';
import { useSimple } from './state/simple';
import { HistoryPanel } from './ui/panels/HistoryPanel';
import { ExportDialog } from './ui/dialogs/ExportDialog';
import { useUi } from './state/ui';
import { Filmstrip } from './ui/filmstrip/Filmstrip';
import { CullOverlay } from './ui/canvas/CullOverlay';
import { folderFromDrop } from './io/fs';
import { openFsaFolder } from './io/open';
import { SyncDialog } from './ui/dialogs/SyncDialog';
import { Toast } from './ui/dialogs/Toast';
import { UpdateBanner } from './ui/dialogs/UpdateBanner';
import { useWorkspace } from './state/workspace';
import { DesignWorkspace } from './design/ui/DesignWorkspace';
import { useClipboard } from './state/clipboard';
import { PresetsPanel } from './ui/panels/PresetsPanel';
import { runAutoFix } from './features/actions';
import { checkCapabilities, type Capabilities } from './engine/gl/capabilities';
import { useKeyboard } from './app/useKeyboard';
import { useLibrary } from './state/library';
import { useLang, useT, type MessageKey } from './i18n';
import type { Histogram } from './engine/histogram';
import s from './app/Shell.module.css';

// Panels that still render as empty sections until their milestone lands.
const LATER: MessageKey[] = [];

export function App() {
  const t = useT();
  const lang = useLang((st) => st.lang);
  const [caps, setCaps] = useState<Capabilities | null>(null);
  const [hist, setHist] = useState<Histogram | null>(null);
  const onHistogram = useCallback((h: Histogram | null) => setHist(h), []);
  const photos = useLibrary((st) => st.photos);
  const mode = useSimple((st) => st.mode);
  const exportOpen = useUi((st) => st.exportOpen);
  const picker = useClipboard((st) => st.picker);
  const cull = useLibrary((st) => st.cull);
  const fallback = useLibrary((st) => st.folder?.mode === 'fallback');
  const [dragOver, setDragOver] = useState(false);
  const ws = useWorkspace((st) => st.ws);
  useKeyboard();
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);
  useEffect(() => {
    checkCapabilities().then(setCaps, () =>
      setCaps({ ok: false, missing: ['WebGL2'], maxTextureSize: 0, floatLinear: false, renderer: '' }),
    );
  }, []);
  // Plating (M16): same top bar, its own layout below it.
  if (ws === 'plating')
    return (
      <div className={s.app} data-testid="shell">
        <TopBar />
        <PlatingWorkspace />
        <AiSettingsDialog />
        <Toast />
        <UpdateBanner />
        <CheatSheet />
        <AboutDialog />
      </div>
    );
  // Design workspace (DRAW_PLAN G1): same top bar, its own layout below it.
  if (ws === 'design')
    return (
      <div className={s.app} data-testid="shell">
        <TopBar />
        <DesignWorkspace />
        <AiSettingsDialog />
        <Toast />
        <UpdateBanner />
        <CheatSheet />
        <AboutDialog />
      </div>
    );
  return (
    <div
      className={`${s.app} ${cull ? s.cull : ''} ${mode === 'simple' ? s.simple : ''} ${mode === 'simple' && photos.length < 2 ? s.simpleOne : ''}`}
      data-testid="shell"
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={async (e) => {
        e.preventDefault();
        setDragOver(false);
        const f = await folderFromDrop(e.dataTransfer.items);
        if (f) await openFsaFolder(f);
      }}
    >
      <TopBar />
      <aside className={`${s.left} ${s.leftOpen}`} aria-label={t('history')}>
        <PanelSection title="history" defaultOpen>
          <HistoryPanel />
        </PanelSection>
        <PanelSection title="presets">
          <PresetsPanel />
        </PanelSection>
      </aside>
      <main className={s.center}>
        {fallback && <div className={s.banner}>{t('fallbackBanner')}</div>}
        {caps?.ok !== false && <Viewer onHistogram={onHistogram} />}
        {cull && <CullOverlay />}
        {dragOver && <div className={s.drop}>{t('dropFolder')}</div>}
      </main>
      {mode === 'simple' ? (
        <aside className={s.right} data-testid="simple-aside">
          <SimplePanel />
        </aside>
      ) : (
        <aside className={s.right}>
          <div style={{ padding: '8px 12px 0' }}>
            <HistogramView hist={hist} />
          </div>
          <button
            className={s.autoFix}
            disabled={!photos.length}
            onClick={() => void runAutoFix()}
            data-testid="auto-fix"
          >
            {t('autoFix')}
          </button>
          <PanelSection title="panelLight" defaultOpen help="light">
            <LightPanel />
          </PanelSection>
          <PanelSection title="panelWhiteBalance" defaultOpen help="white-balance">
            <WbPanel />
          </PanelSection>
          <PanelSection title="panelColor" help="color">
            <ColorPanel />
          </PanelSection>
          <PanelSection title="panelHsl" help="color">
            <HslPanel />
          </PanelSection>
          <PanelSection title="panelCurve" help="tone-curve">
            <CurvePanel />
          </PanelSection>
          <PanelSection title="panelDetail" help="detail">
            <DetailPanel />
          </PanelSection>
          <PanelSection title="panelEffects" help="effects">
            <EffectsPanel />
          </PanelSection>
          <PanelSection title="panelFood" help="food-looks">
            <FoodPanel />
          </PanelSection>
          <PanelSection title="panelLut" help="lut">
            <LutPanel />
          </PanelSection>
          <PanelSection title="panelBackground" help="background">
            <BackgroundPanel />
          </PanelSection>
          <PanelSection title="panelMasks" help="masks">
            <MasksPanel />
          </PanelSection>
          <PanelSection title="panelHeal" help="heal">
            <HealPanel />
          </PanelSection>
          <PanelSection title="panelSkin" help="skin">
            <SkinPanel />
          </PanelSection>
          <PanelSection title="panelCleanWhites" help="clean-whites">
            <CleanWhitesPanel />
          </PanelSection>
          <PanelSection title="panelGeometry" help="geometry">
            <GeometryPanel />
          </PanelSection>
          <PanelSection title="panelUpscale" help="upscale">
            <UpscalePanel />
          </PanelSection>
          <PanelSection title="panelAi" help="ai">
            <AiPhotoButtons big={false} />
            <button style={{ marginTop: 6 }} onClick={() => useAiUi.setState({ settingsOpen: true })}>
              {t('aiSettings')}
            </button>
          </PanelSection>
          <PanelSection title="panelPresets" help="presets">
            <PresetsPanel />
          </PanelSection>
          {LATER.map((p) => (
            <PanelSection key={p} title={p} />
          ))}
        </aside>
      )}
      <footer className={s.film}>{photos.length === 0 ? t('filmstripEmpty') : <Filmstrip />}</footer>
      <SyncDialog key={picker ?? 'none'} />
      <Toast />
      <UpdateBanner />
      <CheatSheet />
      <AboutDialog />
      <AiSettingsDialog />
      <AiCompareDialog />
      {exportOpen && <ExportDialog onClose={() => useUi.getState().set({ exportOpen: false })} />}
      {caps && !caps.ok && <UnsupportedDialog missing={caps.missing} />}
    </div>
  );
}
